import React, { useCallback, useState } from 'react';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card } from '@swyftx/aviary/atoms/Card';
import { Chip } from '@swyftx/aviary/atoms/Chip';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { ShopItem } from '@swyftx/features/shop/api/types';

import { FiatCodeEnum } from '@shared/enums';
import { formatCurrency } from '@shared/utils';
import { cn } from '@shared/utils/lib/ui';

import { useMarkets } from 'src/lib/markets/hooks/useMarkets';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';

import { BuyViaLedgerButton } from '../BuyViaLedgerButton';
import { ShopPreviewImageCard } from '../ShopPreviewImageCard';

type Props = {
  shopItem: ShopItem;
  className?: string;
};

const ShopItemCard: React.FC<Props> = ({ shopItem, className }) => {
  const { navigate } = useNavigateRoute();
  const { getAssetByCode } = useMarkets();
  const [selectedVariant, setSelectedVariant] = useState<number>(0);

  const navigateToItem = useCallback(() => {
    navigate(NavigationURLs.ShopItem, { pathParams: { itemId: shopItem.id }, state: { selectedVariant } });
  }, [navigate, selectedVariant, shopItem.id]);

  return (
    <Card className={cn('p-32', className)} id={shopItem.id}>
      <FlexLayout spacing={16} direction='column'>
        <div>
          <ShopPreviewImageCard
            shopItem={shopItem}
            className='h-[300px]'
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
          />
        </div>

        <FlexLayout direction='column' spacing={16}>
          <FlexLayout alignItems='center' justifyContent='space-between'>
            <FlexLayout direction='column' spacing={8}>
              <FlexLayout direction='row' spacing={8} className='flex items-center'>
                <Heading size='h6'>{shopItem.title}</Heading>
                {shopItem.popular && <Chip size='sm'>Popular</Chip>}
              </FlexLayout>

              <Body size='small' color='secondary'>
                {formatCurrency(shopItem.price, getAssetByCode(FiatCodeEnum.AUD), {
                  appendCode: true,
                  hideCode: false,
                })}
              </Body>
            </FlexLayout>
            <Button variant='outlined' size='md' color='primary' onClick={navigateToItem}>
              Learn more
            </Button>
          </FlexLayout>
          <FlexLayout spacing={16} justifyContent='space-between' className='flex-wrap'>
            <BuyViaLedgerButton shopItemVariant={shopItem.variants[selectedVariant]} />
          </FlexLayout>
        </FlexLayout>
      </FlexLayout>
    </Card>
  );
};

export { ShopItemCard };
